import React, { useMemo } from 'react';

import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import type { IconColor } from '@atlaskit/tokens/css-type-schema';
import Tooltip from '@atlaskit/tooltip/Tooltip';

const borderRadius = token('radius.small');
const defaultBarColor = token('color.background.neutral.bold');

const styles = cssMap({
	container: {
		width: '100%',
		height: '6px',
		position: 'relative',
		backgroundColor: token('color.background.neutral'),
		borderRadius: token('radius.small'),
		overflow: 'hidden',
	},
	segment: {
		display: 'block',
		height: '6px',
		position: 'absolute',
		transition: 'width 0.2s, left 0.2s',
	},
});

const getSegmentBorderRadius = (isFirst: boolean, isLast: boolean): string => {
	if (isFirst && isLast) {
		return borderRadius;
	}
	if (isFirst) {
		return `${borderRadius} 0 0 ${borderRadius}`;
	}
	if (isLast) {
		return `0 ${borderRadius} ${borderRadius} 0`;
	}
	return '0';
};

export interface ProgressSegment {
	count: number;
	color?: IconColor;
	label?: string;
}

export interface MultiColorProgressBarProps {
	segments: ProgressSegment[];
	total: number;
	ariaLabel?: string;
	testId?: string;
}

export const MultiColorProgressBar = ({
	segments,
	total,
	ariaLabel,
	testId = 'multi-color-progress-bar',
}: MultiColorProgressBarProps): React.JSX.Element => {
	const clampedTotal = Math.max(total, 0);

	// Precalculate segments
	const segmentLayout = useMemo(() => {
		const activeIndices = segments.reduce<Set<number>>((set, segment, index) => {
			if (segment.count > 0) {
				set.add(index);
			}
			return set;
		}, new Set());

		let cumulative = 0;
		let activeIndex = 0;
		const activeCount = activeIndices.size;

		return segments.map((segment, index) => {
			const isActive = activeIndices.has(index);
			const widthPercent = isActive && clampedTotal > 0 ? (segment.count / clampedTotal) * 100 : 0;
			const leftPercent = cumulative;
			cumulative += widthPercent;

			const isFirstActive = isActive && activeIndex === 0;
			const isLastActive = isActive && activeIndex === activeCount - 1;
			const gap = isActive && !isFirstActive ? 1 : 0;

			if (isActive) {
				activeIndex++;
			}

			return {
				widthPercent,
				leftPercent,
				color: segment.color,
				label: segment.label,
				isFirst: isFirstActive,
				isLast: isLastActive,
				gap,
			};
		});
	}, [segments, clampedTotal]);

	// Used only for aria-valuenow
	const totalCount = useMemo(
		() => segmentLayout.reduce((sum, segment) => sum + segment.widthPercent, 0),
		[segmentLayout],
	);

	return (
		<div
			css={styles.container}
			role="progressbar"
			aria-label={ariaLabel}
			aria-valuemin={0}
			aria-valuenow={clampedTotal > 0 ? Math.round((totalCount / 100) * clampedTotal) : 0}
			aria-valuemax={clampedTotal}
			data-testid={testId}
		>
			{clampedTotal > 0 &&
				segmentLayout.map((layout, index) => {
					const segment = (
						<span
							css={styles.segment}
							style={{
								left: `calc(${layout.leftPercent}% + ${layout.gap}px)`,
								width: `calc(${layout.widthPercent}% - ${layout.gap}px)`,
								backgroundColor: layout.color ?? defaultBarColor,
								borderRadius: getSegmentBorderRadius(layout.isFirst, layout.isLast),
							}}
							data-testid={`${testId}--segment-${index}`}
						/>
					);

					return layout.label ? (
						<Tooltip key={index} content={layout.label}>
							{segment}
						</Tooltip>
					) : (
						<React.Fragment key={index}>{segment}</React.Fragment>
					);
				})}
		</div>
	);
};
